import React, { useContext, useReducer } from 'react';
import axios from 'axios';
import MMMContext from './MMMContext';
import MMMReducer from './MMMReducer';
import AuthContext from '../auth/AuthContext';
import MainContext from '../main/MainContext';
import { MMMTYPE } from '../../components/Utilities/NamingConversions';
import cloneDeep from 'lodash.clonedeep';
import { v4 as uuidv4 } from 'uuid';
import { trackerService } from '@api/tracker';
import { EVENT_NAMES } from '@constants/trackingConstants';
import { convertCurrencyHistorical } from '../../components/MLModel/Util/CommonFunctions';
import { openPopup } from '@utils/popup';

import {
  RESET_OMM_STATE,
  RESET_STATE,
  SET_ACTIVE_ADMIN_MODEL,
  SET_ACTIVE_MODEL,
  SET_ADSTOCK_IMPACT,
  SET_CATEGORIES,
  SET_CHOOSEN_MEDIA_RANGE,
  SET_COMPARED_MEDIA,
  SET_CURRENCY,
  SET_END_DATE_AND_INDEX,
  SET_EXCHANGE_RATES,
  SET_EXECUTIVE_MMM_DATA,
  SET_EXPORT_INTEGRATIONS,
  SET_FEATURES,
  SET_FORECAST_END_DATE,
  SET_FORECAST_MODEL_DATES,
  SET_FORECAST_RANGE,
  SET_GROUPING_VALUE,
  SET_MEDIA,
  SET_MEDIA_ENTITIES,
  SET_METRIC,
  SET_MIN_MAX_MEDIA,
  SET_MMM_DATA_AFTER_TOGGLE_UPDATE,
  SET_MODEL_BUILD_DATE,
  SET_MODEL_DATES,
  SET_OMM_DATA,
  SET_OMM_GRAPH_DATA,
  SET_PREFIX_SYMBOLS,
  SET_PROMOTION_SLIDER_INFO,
  SET_ROAS_PROJECTIONS_MMM_DATA,
  SET_SELECTED_CATEGORIES,
  SET_START_DATE_AND_INDEX,
  SET_SYNERGY,
  SET_TITLE,
  SET_TOGGLES_TARGETS,
  SET_VERSION,
  SET_WORKFLOW_COMPLETE,
  SET_WORKFLOW_ID,
} from '../types';

function MMMState(props) {
  const authContext = useContext(AuthContext);
  const mainContext = useContext(MainContext);
  const { loadingDecrement, loadingIncrement } = mainContext;

  const { defaultMinDate, defaultMaxDate } = {
    defaultMinDate: new Date('1900-01-01T17:00:00'),
    defaultMaxDate: new Date(), // Date() returns today's date
  };
  const defaultPrefixSymbols = {
    spend: '$', // default symbol for spend
    return: '$', // default symbol for returns
    roas: '$', // default symbol for ROAS/CPA
  };

  const initialState = {
    sales: {},
    activeModel: false,
    activeAdminModel: {},
    roasData: {}, //roas -- used in (1) ROAS Projections
    weeklyData: [], //breakdown
    salesData: {}, //salesgraph
    roiData: [], // curves -- used in (1) ROAS Projections
    campaignCurve: {}, // overall campaign curve
    grouping: null, // a.k.a "hidden"
    medias: [], // list of medias -- use in (1) ROAS Proejections
    ommData: [], // optimal media mix channels
    ommMax: 0,
    ommMin: 0,
    ommGraphData: {}, // optimal media mix data
    adstockImpact: {},
    minDate: defaultMinDate,
    maxDate: defaultMaxDate,
    startDate: defaultMinDate,
    endDate: defaultMaxDate,
    forecastMinDate: defaultMinDate,
    forecastMaxDate: defaultMaxDate,
    forecastEndDate: defaultMaxDate,
    startIndex: 0,
    endIndex: 0,
    filterMedia: 'None',
    filterMediaMinMax: [0, 1],
    filterMediaChoosenRange: [-1, -1],
    groupingValue: null,
    toggleTargets: {},
    selectedGroupingValue: {},
    features: {},
    title: '',
    comparedMedia: {},
    forecastRange: [0, 0],
    synergy: null,
    synergyNew: null,
    synergyDelta: null,
    exchangeRates: {},
    currency: {},
    prefixSymbols: defaultPrefixSymbols,
    metric: {},
    modelBuildDate: '',
    categories: {},
    selectedCategories: [],
    promotionSliderInfo: {},
    mediaEntities: {},
    version: 0,
    workflowId: null, // for tracking sequence of events
    workflowCompleted: false, // for tracking clean workflow completion
    exportIntegrations: [], // export integrations of client-configured cloud database(s)
  };

  const [state, dispatch] = useReducer(MMMReducer, {
    ...initialState,
  });

  // Method to set an active model
  const setActiveModel = (activeModel) => {
    resetState();
    dispatch({ type: SET_ACTIVE_MODEL, payload: activeModel });
  };

  // Method to set a selected MMM for admin menu (for "Build Config" or "Build MMM" options)
  const setActiveAdminModel = (activeModel) => {
    resetState();
    dispatch({ type: SET_ACTIVE_ADMIN_MODEL, payload: activeModel });
  };

  // A function to set minimum date, maximum date, start data, and end date.
  // used within MMMContext.
  function setModelDates(mmmDataArray) {
    const minDate = new Date(mmmDataArray[0] + 'T17:00:00');
    const startDate = minDate;
    const endIndex = mmmDataArray.length;
    const maxDate = new Date(
      mmmDataArray[mmmDataArray.length - 1] + 'T17:00:00'
    );
    const endDate = maxDate;
    dispatch({
      type: SET_MODEL_DATES,
      payload: { minDate, startDate, maxDate, endDate, endIndex },
    });
  }

  // function to set StartDate and startIndex:

  function setStartDate(date) {
    const startDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      17,
      0,
      0
    );
    const datesArray = state.salesData.dates.historical;
    let startIndex = datesArray.length;
    for (let i = 0; i < datesArray.length; i++) {
      if (new Date(`${datesArray[i]}T17:00:00`) >= startDate) {
        startIndex = i;
        break;
      }
    }
    dispatch({
      type: SET_START_DATE_AND_INDEX,
      payload: { startDate, startIndex },
    });
  }

  // function to set EndDate and EndIndex:

  function setEndDate(date) {
    const endDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      17,
      0,
      0
    );
    const datesArray = state.salesData.dates.historical;
    let endIndex = 0;
    for (let i = datesArray.length - 1; i >= 0; i--) {
      if (new Date(`${datesArray[i]}T17:00:00`) <= endDate) {
        endIndex = i + 1;
        break;
      }
    }
    dispatch({
      type: SET_END_DATE_AND_INDEX,
      payload: { endDate, endIndex },
    });
  }

  function setForecastDates(mmmDataArray, forecastNumWeeks) {
    const forecastMinDate = new Date(mmmDataArray[0] + 'T17:00:00');
    const forecastMaxDate = new Date(
      mmmDataArray[mmmDataArray.length - 1] + 'T17:00:00'
    );
    const forecastEndDate = new Date(
      mmmDataArray[forecastNumWeeks - 1] + 'T17:00:00'
    );
    setForecastRange([0, forecastNumWeeks]);
    dispatch({
      type: SET_FORECAST_MODEL_DATES,
      payload: { forecastMinDate, forecastMaxDate, forecastEndDate },
    });
  }

  function setForecastEndDate(date) {
    const forecastEndDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      17,
      0,
      0
    );
    const datesArray = state.salesData.dates.future;
    let forecastEndIndex = 0;
    for (let i = datesArray.length - 1; i >= 0; i--) {
      if (new Date(`${datesArray[i]}T17:00:00`) <= forecastEndDate) {
        forecastEndIndex = i + 1;
        break;
      }
    }
    setForecastRange([0, forecastEndIndex]);
    dispatch({
      type: SET_FORECAST_END_DATE,
      payload: forecastEndDate,
    });
  }

  // a helper function to check for groupings and add 'use' parameter to each roas.param element
  // as well as add items from groupings to roas.spent and roas.returns.
  function groupingROAS(roas, grouping) {
    if (roas === undefined) {
      return;
    }
    const updatedRoas = cloneDeep(roas);
    Object.keys(roas.params).forEach((mediaName) => {
      // eslint-disable-line
      updatedRoas.params[mediaName]['use'] = 1;
    });
    // As there is a complex nested structure, we have to go through
    // Array of objects, to get object, then go throught each object (toggleGroupItems)
    // to add grouping paramteres to main roas
    if (grouping === undefined) return updatedRoas;
    grouping.params.forEach((toggleGroup, index) => {
      // eslint-disable-line
      // For example: 'TV'
      const toggleTarget = toggleGroup['target'];
      // For example: 'English TV'
      const toggleName = toggleGroup['toggle_name'];
      const toggleGroupItems = toggleGroup['params'];
      Object.keys(toggleGroupItems).forEach((groupItem) => {
        // eslint-disable-line
        updatedRoas.params[groupItem] = toggleGroupItems[groupItem];
        updatedRoas.params[groupItem]['use'] = 0;
        updatedRoas.params[groupItem]['target'] = toggleTarget;
        updatedRoas.params[groupItem]['group'] = toggleName;
        // Add spent to roas from grouping:
        updatedRoas.spent[groupItem] =
          grouping.spent[index]['spent'][groupItem];
        // Add return to roas from grouping:
        updatedRoas.returns[groupItem] =
          grouping.returns[index]['returns'][groupItem];
      });
    });
    return updatedRoas;
  }

  // a helper function to check for groupings and add 'use' parameter to each roi(aka curves) element
  // as well as add items from groupings to roas.spent and roas.returns.
  function groupingROI(roiData, grouping) {
    if (roiData === undefined) {
      return;
    }
    let updatedRoiData = cloneDeep(roiData);
    updatedRoiData.forEach((mediaName) => {
      // eslint-disable-line
      mediaName['use'] = 1;
    });
    // As there is a complex nested structure, we have to go through
    // Array of objects, to get object, then go throught each object (toggleGroupItems)
    // to add grouping paramteres to main roiData
    if (grouping === undefined) return updatedRoiData;
    grouping.forEach((toggleGroup) => {
      // eslint-disable-line
      // For example: 'TV'
      const toggleTarget = toggleGroup['target'];
      // For example: 'English TV'
      const toggleName = toggleGroup['toggle_name'];
      const toggleGroupItems = toggleGroup['curves'];
      Object.keys(toggleGroupItems).forEach((groupItem) => {
        toggleGroupItems[groupItem]['use'] = 0;
        toggleGroupItems[groupItem]['target'] = toggleTarget;
        toggleGroupItems[groupItem]['group'] = toggleName;
        updatedRoiData.push(toggleGroupItems[groupItem]);
      });
    });
    return updatedRoiData;
  }

  // a helper function to check for groupings and add 'use' parameter to each roi(aka curves) element
  // as well as add items from groupings to roas.spent and roas.returns.
  function groupingWeeklyData(weeklyData, grouping) {
    if (weeklyData === undefined) {
      return;
    }
    let updatedWeeklyData = cloneDeep(weeklyData);
    updatedWeeklyData.forEach((mediaName) => {
      mediaName['use'] = 1;
    });
    // As there is a complex nested structure, we have to go through
    // Array of objects, to get object, then go throught each object (toggleGroupItems)
    // to add grouping paramteres to main roiData
    if (grouping === undefined) return updatedWeeklyData;
    grouping.forEach((toggleGroup) => {
      // For example: 'TV'
      const toggleTarget = toggleGroup['target'];
      // For example: 'English TV'
      const toggleName = toggleGroup['toggle_name'];
      const toggleGroupItems = toggleGroup['contributions'];
      Object.keys(toggleGroupItems).forEach((groupItem) => {
        toggleGroupItems[groupItem]['use'] = 0;
        toggleGroupItems[groupItem]['target'] = toggleTarget;
        toggleGroupItems[groupItem]['group'] = toggleName;
        updatedWeeklyData.push(toggleGroupItems[groupItem]);
      });
    });
    return updatedWeeklyData;
  }

  // a helper function to check for groupings and add 'use' parameter to each roi(aka curves) element
  // as well as add items from groupings to roas.spent and roas.returns.
  function groupingSalesData(sales, grouping) {
    if (sales === undefined) {
      return;
    }
    let updatedSales = cloneDeep(sales);
    let salesData = updatedSales.features;
    let lastQuarterDataLength = 0;
    // salesData['using_last_quarter'] = {}
    Object.keys(salesData.using_last_quarter).forEach((mediaName) => {
      lastQuarterDataLength = salesData['using_last_quarter'][mediaName].length;
      salesData['using_last_quarter'][mediaName] = {
        [mediaName]: salesData['using_last_quarter'][mediaName],
        use: 1,
      };
      salesData['historical'][mediaName] = {
        [mediaName]: salesData['historical'][mediaName],
        use: 1,
      };
    });
    // As there is a complex nested structure, we have to go through
    // Array of objects, to get object, then go throught each object (toggleGroupItems)
    // to add grouping paramteres to main roiData
    if (grouping === undefined) {
      let updatedFeatures = cloneDeep(salesData['using_last_quarter']);
      dispatch({
        type: SET_FEATURES,
        payload: updatedFeatures,
      });
      return updatedSales;
    }
    grouping.forEach((toggleGroup) => {
      // For example: 'TV'
      const toggleTarget = toggleGroup['target'];
      // For example: 'English TV'
      const toggleName = toggleGroup['toggle_name'];
      const toggleGroupItems = toggleGroup['spendings'];
      Object.keys(toggleGroupItems).forEach((groupItem) => {
        salesData['historical'][groupItem] = {
          [groupItem]: toggleGroupItems[groupItem],
          use: 0,
          target: toggleTarget,
          group: toggleName,
        };
        salesData['using_last_quarter'][groupItem] = {
          [groupItem]: toggleGroupItems[groupItem].slice(
            -lastQuarterDataLength
          ),
          use: 0,
          target: toggleTarget,
          group: toggleName,
        };
      });
    });
    let updatedFeatures = cloneDeep(salesData['using_last_quarter']);
    dispatch({
      type: SET_FEATURES,
      payload: updatedFeatures,
    });
    return updatedSales;
  }

  function createTargetGroups() {
    if (state.grouping === undefined) return;
    if (state.grouping !== null && state.groupingValue === null) {
      let toggleTargets = {};
      state.grouping.roas.params.forEach((toggleGroup) => {
        const toggleTarget = toggleGroup['target'];
        const toggleName = toggleGroup['toggle_name'];
        if (toggleTargets.hasOwnProperty(toggleTarget)) {
          toggleTargets[toggleTarget].push(toggleName);
        } else {
          toggleTargets[toggleTarget] = [toggleName];
          dispatch({
            type: SET_GROUPING_VALUE,
            payload: { [toggleTarget]: 'All', key: toggleTarget },
          });
        }
      });
      dispatch({ type: SET_TOGGLES_TARGETS, payload: toggleTargets });
    }
  }

  // ========================================================================
  // ===                                                                  ===
  // ===                START OF ROUTES FOR DYNAMIC TABS                  ===
  // ===                                                                  ===
  // ========================================================================

  // makes request to get executive summary data for a particular MMM.
  // used in the 'Executive Summary' tab.
  async function getMMMData(type = MMMTYPE, activeModel = state.activeModel) {
    try {
      loadingIncrement(type);
      const token = await authContext.getIdToken();
      const res = await axios.post(
        '/get_mmm_data',
        {
          file: activeModel,
          countryCode: authContext.userCountryCode,
          kwargs: mainContext.getPostKWArgs(),
        },
        {
          headers: { Authorization: token },
        }
      );
      let updatedRoas = groupingROAS(res.data.roas, res.data.hidden?.roas);
      let updatedSales = groupingSalesData(
        res.data.sales_graph,
        res.data.hidden?.spendings
      );
      let updatedWeeklyData = groupingWeeklyData(
        res.data.breakdown,
        res.data.hidden?.contributions
      );
      let updatedPromotionSliderInfo = res.data.promotion_slider;
      const forecastNumWeeks =
        res.data.sales_graph.forecast_num_weeks ||
        res.data.sales_graph['dates']['future'].length;
      setModelDates(res.data.sales_graph.dates.historical);
      setForecastDates(res.data.sales_graph.dates.future, forecastNumWeeks);
      let categories = {
        // medias not included since they are dynamically updated with
        // state.medias depending on which subgroups are toggled on
        // yoyGrowth not included since it is not an array of factors
        nonmedias: updatedSales?.nonmedias || [],
        macroeconomics: updatedSales?.macroeconomics || [],
        competitors: updatedSales?.competitors || [],
      };
      categories = Object.entries(categories).reduce(
        (acc, [k, v]) => (v.length > 0 ? { ...acc, [k]: v } : acc),
        {}
      );
      // all categories are selected by default; can be unchecked individually
      let selectedCategories = ['medias', ...Object.keys(categories)];
      if (res.data.version >= 2.1 && forecastNumWeeks > 0) {
        selectedCategories = ['yoyGrowth', ...selectedCategories];
      }

      // set prefix symbols
      setPrefixSymbols(res.data.exchange_rates.primary, res.data.metric);

      dispatch({
        type: SET_EXECUTIVE_MMM_DATA,
        payload: {
          ...res.data,
          roas: updatedRoas,
          sales_graph: updatedSales,
          breakdown: updatedWeeklyData,
        },
      });
      dispatch({
        type: SET_TITLE,
        payload: res.data.title,
      });
      dispatch({
        type: SET_VERSION,
        payload: res.data.version,
      });
      dispatch({
        type: SET_EXPORT_INTEGRATIONS,
        payload: res.data.export_integrations,
      });
      dispatch({
        type: SET_METRIC,
        payload: res.data.metric,
      });
      dispatch({
        type: SET_MODEL_BUILD_DATE,
        payload: res.data?.last_built,
      });
      dispatch({
        type: SET_ADSTOCK_IMPACT,
        payload: res.data?.adstock_impact,
      });
      dispatch({
        type: SET_CATEGORIES,
        payload: categories,
      });
      dispatch({
        type: SET_SELECTED_CATEGORIES,
        payload: selectedCategories,
      });
      dispatch({
        type: SET_PROMOTION_SLIDER_INFO,
        payload: updatedPromotionSliderInfo,
      });
      dispatch({
        type: SET_SYNERGY,
        payload: res.data.synergy,
      });
      dispatch({
        type: 'SET_SYNERGY_NEW',
        payload: res.data.synergy_new,
      });
      dispatch({
        type: 'SET_SYNERGY_DELTA',
        payload: res.data.synergy_delta,
      });
      dispatch({
        type: SET_EXCHANGE_RATES,
        payload: res.data.exchange_rates,
      });
      dispatch({
        type: SET_CURRENCY,
        payload: res.data.exchange_rates.primary,
      });
      dispatch({
        type: SET_MEDIA_ENTITIES,
        payload: res.data.media_entities,
      });

      return res.status;
    } catch (err) {
      if (err.response?.status !== 404) {
        // 404 is expected the selected model no longer exists, so we
        // handle that error in the calling component itself
        mainContext.handleError(err);
      }
      return err.response?.status;
    } finally {
      loadingDecrement(type);
    }
  }

  // makes request to get ROAS Projection Data for a particular MMM.
  // used in the 'ROAS Projections' tab.

  async function getROASProjectionsData(type = MMMTYPE) {
    try {
      loadingIncrement(type);
      const token = await authContext.getIdToken();
      const res = await axios.post(
        '/retrieve_roas_projection_data',
        {
          file: state.activeModel,
          countryCode: authContext.userCountryCode,
          kwargs: mainContext.getPostKWArgs(),
        },
        {
          headers: { Authorization: token },
        }
      );
      let updatedRoi = groupingROI(res.data.curves, res.data.hidden?.curves);
      let updatedCampaignCurve = res.data.campaign_curve;
      dispatch({
        type: SET_ROAS_PROJECTIONS_MMM_DATA,
        payload: {
          ...res.data,
          curves: updatedRoi, // curves is a 'legacy/backend' name for ROI
          campaignCurve: updatedCampaignCurve,
        },
      });
    } catch (err) {
      mainContext.handleError(err);
    } finally {
      loadingDecrement(type);
    }
  }

  async function getOMMData(type = MMMTYPE) {
    try {
      loadingIncrement(type);
      const token = await authContext.getIdToken();
      const res = await axios.post(
        '/get_default_media_mix_bounds',
        {
          file: state.activeModel,
          countryCode: authContext.userCountryCode,
          channel_list: state.medias,
          kwargs: mainContext.getPostKWArgs(),
        },
        {
          headers: { Authorization: token },
        }
      );
      // convert bounds to currently selected currency
      let convertedBounds = res.data.bounds.map((media) => {
        return {
          name: media.name,
          min: Math.floor(media.min * state.currency.average),
          max: Math.floor(media.max * state.currency.average),
        };
      });
      let convertedData = {
        bounds: convertedBounds,
        global_max: Math.ceil(res.data.global_max * state.currency.average),
        global_min: Math.ceil(res.data.global_min * state.currency.average),
      };
      dispatch({
        type: SET_OMM_DATA,
        payload: convertedData,
      });
    } catch (err) {
      mainContext.handleError(err);
    } finally {
      loadingDecrement(type);
    }
  }

  async function getOMMGraphData(
    view,
    advancedBounds,
    clientSimpleBudget,
    type = MMMTYPE
  ) {
    try {
      loadingIncrement(type);
      const token = await authContext.getIdToken();
      // convert bounds to original primary currency
      let convertedAdvancedBounds = advancedBounds.map((media) => {
        return {
          name: media.name,
          min: Math.floor(media.min * (1 / state.currency.average)),
          max: Math.floor(media.max * (1 / state.currency.average)),
        };
      });
      let convertedSimpleBounds = {
        min: Math.floor(clientSimpleBudget.min * (1 / state.currency.average)),
        max: Math.floor(clientSimpleBudget.max * (1 / state.currency.average)),
      };
      const res = await axios.post(
        '/get_custom_media_mix',
        {
          client_bounds: {
            view: view,
            simple_bounds: convertedSimpleBounds,
            advanced_bounds: convertedAdvancedBounds,
          },
          file: state.activeModel,
          countryCode: authContext.userCountryCode,
          kwargs: mainContext.getPostKWArgs(),
        },
        {
          headers: { Authorization: token },
        }
      );
      dispatch({
        type: SET_OMM_GRAPH_DATA,
        payload: res.data.media_mix,
      });
    } catch (err) {
      mainContext.handleError(err);
    } finally {
      loadingDecrement(type);
    }
  }

  // ========================================================================
  // ===                                                                  ===
  // ===                END OF ROUTES FOR DYANAMIC TABS                   ===
  // ===                                                                  ===
  // ========================================================================

  const exportInputDataLocal = async (sheet) => {
    try {
      loadingIncrement(MMMTYPE);
      const token = await authContext.getIdToken();
      const { data } = await axios.post(
        '/export_mmm_input_file',
        {
          file: state.activeModel,
          sheet: sheet,
          countryCode: authContext.userCountryCode,
          kwargs: mainContext.getPostKWArgs(),
        },
        {
          headers: { Authorization: token },
        }
      );
      openPopup(data.status);
      return data;
    } catch (err) {
      // handle 403 by main context; calling component handles other errors
      if (err.response?.status === 403) {
        mainContext.handleError(err);
      } else {
        throw err;
      }
    } finally {
      loadingDecrement(MMMTYPE);
    }
  };

  const exportMMMDataLocal = async () => {
    try {
      loadingIncrement(MMMTYPE);
      const token = await authContext.getIdToken();
      const { data } = await axios.post(
        '/export_mmm_data_local',
        {
          file: state.activeModel,
          countryCode: authContext.userCountryCode,
          kwargs: mainContext.getPostKWArgs(),
        },
        {
          headers: { Authorization: token },
        }
      );
      openPopup(data.status);
      return data;
    } catch (err) {
      if (err.message !== 'Pop-ups are blocked') {
        mainContext.handleError(err);
      }
      throw err;
    } finally {
      loadingDecrement(MMMTYPE);
    }
  };

  const exportMMMDataCloud = async (integrationId, subdirectory) => {
    try {
      loadingIncrement(MMMTYPE);
      const token = await authContext.getIdToken();
      const { data } = await axios.post(
        '/export_mmm_data_cloud',
        {
          file: state.activeModel,
          countryCode: authContext.userCountryCode,
          kwargs: mainContext.getPostKWArgs(),
          integration_id: integrationId,
          subdirectory: subdirectory,
        },
        {
          headers: { Authorization: token },
        }
      );
      return data;
    } catch (err) {
      const response = err?.response;
      if (response?.status !== 401) {
        // handle 401 error in the calling component itself
        mainContext.handleError(err);
      }
      return response?.data;
    } finally {
      loadingDecrement(MMMTYPE);
    }
  };

  const getTabFilterUniqueValues = async (tabName) => {
    // get a dictionary of all the unique values for each column in the specified tab
    try {
      const token = await authContext.getIdToken();
      return await axios.post(
        '/get_tab_unique_filter_values',
        {
          file: state.activeModel,
          tabName: tabName,
          countryCode: authContext.userCountryCode,
          kwargs: mainContext.getPostKWArgs(),
        },
        {
          headers: { Authorization: token },
        }
      );
    } catch (err) {
      return err.response;
    }
  };

  const getSpreadsheet = async (activeModel, spendings, type = MMMTYPE) => {
    try {
      loadingIncrement(type);
      const token = await authContext.getIdToken();
      return await axios.post(
        '/get_spending_sheet',
        {
          file: activeModel,
          spendings_info: spendings,
          countryCode: authContext.userCountryCode,
          kwargs: mainContext.getPostKWArgs(),
        },
        {
          headers: { Authorization: token },
        }
      );
    } catch (err) {
      mainContext.handleError(err);
      return err.response;
    } finally {
      loadingDecrement(type);
    }
  };

  const setSpreadsheet = async (activeModel, type = MMMTYPE) => {
    try {
      loadingIncrement(type);
      const token = await authContext.getIdToken();
      const res = await axios.post(
        '/post_spending_sheet',
        {
          file: activeModel,
          countryCode: authContext.userCountryCode,
          kwargs: mainContext.getPostKWArgs(),
        },
        {
          headers: { Authorization: token },
        }
      );
      return res.data;
    } catch (err) {
      mainContext.handleError(err);
      return err.response;
    } finally {
      loadingDecrement(type);
    }
  };

  // A function to reset MMMContext state to its initial state.
  const resetState = () => {
    dispatch({ type: RESET_STATE, payload: initialState });
  };

  // A function to reset OMMContext state to its initial state if view is changed.
  const resetOMMState = () => {
    dispatch({ type: RESET_OMM_STATE, payload: initialState });
  };

  // A function to set minimum and maximum value range for selected Filter Media
  // in Adjust Options pop up for Filter Media slider
  const setFilterMediaAndRange = (filterMedia) => {
    let minRange = -1;
    let maxRange = 1;
    let upperBound = maxRange;
    if (filterMedia !== 'None') {
      let mediaData =
        state.salesData['features']['historical'][filterMedia][filterMedia];
      if (Object.keys(state.currency).length) {
        mediaData = convertCurrencyHistorical(mediaData, state.currency);
      }
      minRange = Math.min(...mediaData);
      maxRange = Math.max(...mediaData);
      upperBound = Math.ceil(maxRange);
    }
    dispatch({ type: SET_MEDIA, payload: filterMedia });
    dispatch({ type: SET_MIN_MAX_MEDIA, payload: [0, upperBound] });
    dispatch({
      type: SET_CHOOSEN_MEDIA_RANGE,
      payload: [minRange, maxRange],
    });
  };

  // Used to update choosenRange when user moved Filter Media slider
  const setRangeForMedia = (range) => {
    dispatch({ type: SET_CHOOSEN_MEDIA_RANGE, payload: range });
  };

  // Used to set currency in the 'Adjust Options' tab:
  const setCurrency = (currencyName) => {
    const exchangeRates = state.exchangeRates;
    if (!Object.keys(exchangeRates).length) return;
    const allCurrencies = [exchangeRates.primary, ...exchangeRates.secondary];
    const chosenCurrency = allCurrencies.find(
      (currency) => currency.name === currencyName
    );

    dispatch({ type: SET_CURRENCY, payload: chosenCurrency });
    setPrefixSymbols(chosenCurrency, state.metric);
  };

  // Used to set prefix symbols for spend, return, and ROAS/CPA
  const setPrefixSymbols = (currency, metric) => {
    // Central place to modify prefix symbols for all tabs in MMM client module
    const prefixSymbols = {
      spend: currency?.symbol,
      return: metric?.type === 'Sales' ? currency?.symbol : metric?.symbol,
      roas: currency?.symbol, // Assuming volume-based models use CPA (cost per acquisition)
    };

    dispatch({
      type: SET_PREFIX_SYMBOLS,
      payload: prefixSymbols,
    });
  };

  const setSelectedCategories = (categories) => {
    if (categories.includes('medias')) {
      let medias = state.medias;
      let features = cloneDeep(state.features);
      let salesData = state.salesData;
      medias.forEach((media) => {
        features[media] = salesData.features['using_last_quarter'][media];
      });
      dispatch({ type: SET_FEATURES, payload: features });
    }
    dispatch({ type: SET_SELECTED_CATEGORIES, payload: categories });
  };

  // Used to set groupingValue in the 'Adjust Options' tab:
  const setGroupingValue = (value, target) => {
    const updatedData = updateShowTagBasedOnNewGroupingValue(target, value);

    dispatch({
      type: SET_MMM_DATA_AFTER_TOGGLE_UPDATE,
      payload: updatedData,
    });

    dispatch({
      type: SET_GROUPING_VALUE,
      payload: { [target]: value, key: target },
    });
    return updatedData;
  };

  // Function used to mutate data when toggle selection changes
  const updateShowTagBasedOnNewGroupingValue = (target, value) => {
    // ROAS mutation:
    let updatedRoas = cloneDeep(state.roasData);
    let updatedMediaList = cloneDeep(state.medias);
    // This case is when mutated data is object of objects (also mutates state.medias)
    Object.keys(updatedRoas.params).forEach((mediaKey) => {
      // If target toggle selected as 'All', the grouping values will have their use = 0
      // and the rest will have use = 1. This is similar to the case if no grouping was provided.
      if (value === 'All') {
        if (mediaKey === target) {
          updatedRoas.params[mediaKey]['use'] = 1;
          if (updatedMediaList.indexOf(mediaKey) === -1) {
            updatedMediaList.push(mediaKey);
          }
        } else if (updatedRoas.params[mediaKey]['target'] === target) {
          updatedRoas.params[mediaKey]['use'] = 0;
          if (updatedMediaList.indexOf(mediaKey) > -1) {
            updatedMediaList.splice(updatedMediaList.indexOf(mediaKey), 1);
          }
        }
      } else {
        // This selection goes through media keys and check if 'target' (i.e. TV) is one of the media
        // It will mark 'TV' as 0 for 'use', while everything that has 'target' as TV and group, for example, 'Language'
        // will be marked as 1 for 'use'.
        if (mediaKey.toUpperCase() === target.toUpperCase()) {
          updatedRoas.params[mediaKey]['use'] = 0;
          if (updatedMediaList.indexOf(mediaKey) > -1)
            updatedMediaList.splice(updatedMediaList.indexOf(mediaKey), 1);
        } else if (
          updatedRoas.params[mediaKey]['target']?.toUpperCase() ===
            target.toUpperCase() &&
          updatedRoas.params[mediaKey]['group']?.toUpperCase() ===
            value.toUpperCase()
        ) {
          updatedRoas.params[mediaKey]['use'] = 1;
          if (updatedMediaList.indexOf(mediaKey) === -1)
            updatedMediaList.push(mediaKey);
        } else if (
          !!updatedRoas.params[mediaKey]['target'] &&
          updatedRoas.params[mediaKey]['target'].toUpperCase() ===
            target.toUpperCase()
        ) {
          updatedRoas.params[mediaKey]['use'] = 0;
          if (updatedMediaList.indexOf(mediaKey) > -1)
            updatedMediaList.splice(updatedMediaList.indexOf(mediaKey), 1);
        }
      }
    });
    // ROI mutation
    // This case is when mutated data is object of objects
    let updatedRoi = cloneDeep(state.roiData);
    updatedRoi.forEach((mediaObject) => {
      // If target toggle selected as 'All', the grouping values will have their use = 0
      // and the rest will have use = 1. This is similar to the case if no grouping was provided.
      if (value === 'All') {
        // Check if object has 'target' attribute which means it is a sub-group object
        if (!!mediaObject['target'] && mediaObject['target'] === target) {
          mediaObject['use'] = 0;
        } else {
          // Make sure that only 'parent' target component is being updated.
          if (mediaObject['name'] === target) mediaObject['use'] = 1;
        }
      } else {
        // This selection goes through media keys and check if 'target' (i.e. TV) is one of the media
        // It will mark 'TV' as 0 for 'use', while everything that has 'target' as TV and group, for example, 'Language'
        // will be marked as 1 for 'use'.
        if (mediaObject['name'].toUpperCase() === target.toUpperCase()) {
          mediaObject['use'] = 0;
        } else if (
          mediaObject['target']?.toUpperCase() === target.toUpperCase() &&
          mediaObject['group']?.toUpperCase() === value.toUpperCase()
        ) {
          mediaObject['use'] = 1;
        } else if (
          !!mediaObject['target'] &&
          mediaObject['target'].toUpperCase() === target.toUpperCase()
        )
          mediaObject['use'] = 0;
      }
    });
    // salesData mutation. Need to update features (using last quarter) and medias.
    // use spendings when updating salesData.
    let updatedSalesData = cloneDeep(state.salesData);
    let mediaListInSalesData = updatedSalesData.medias;
    let updatedFeatures = updatedSalesData.features.using_last_quarter;
    Object.keys(updatedFeatures).forEach((mediaKey) => {
      // If target toggle selected as 'All', the grouping values will have their use = 0
      // and the rest will have use = 1. This is similar to the case if no grouping was provided.
      if (value === 'All') {
        if (
          updatedFeatures[mediaKey]['target'] === target ||
          mediaKey === target
        ) {
          // Add target back to mediaListInSales if "All" selected in Adjust Options
          if (mediaListInSalesData.indexOf(target) === -1) {
            mediaListInSalesData.push(target);
          }
          if (!!updatedFeatures[mediaKey]['target']) {
            if (mediaListInSalesData.indexOf(mediaKey) > -1) {
              mediaListInSalesData.splice(
                mediaListInSalesData.indexOf(mediaKey),
                1
              );
            }
            updatedFeatures[mediaKey]['use'] = 0;
          } else {
            if (mediaListInSalesData.indexOf(mediaKey) === -1)
              mediaListInSalesData.push(mediaKey);
            updatedFeatures[mediaKey]['use'] = 1;
          }
        }
      } else {
        // This selection goes through media keys and check if 'target' (i.e. TV) is one of the media
        // It will mark 'TV' as 0 for 'use', while everything that has 'target' as TV and group, for example, 'Language'
        // will be marked as 1 for 'use'.
        if (mediaKey.toUpperCase() === target.toUpperCase()) {
          updatedFeatures[mediaKey]['use'] = 0;
          if (mediaListInSalesData.indexOf(mediaKey) > -1)
            mediaListInSalesData.splice(
              mediaListInSalesData.indexOf(mediaKey),
              1
            );
        } else if (
          updatedFeatures[mediaKey]['target']?.toUpperCase() ===
            target.toUpperCase() &&
          updatedFeatures[mediaKey]['group']?.toUpperCase() ===
            value.toUpperCase()
        ) {
          updatedFeatures[mediaKey]['use'] = 1;
          if (mediaListInSalesData.indexOf(mediaKey) === -1)
            mediaListInSalesData.push(mediaKey);
        } else if (
          !!updatedFeatures[mediaKey]['target'] &&
          updatedFeatures[mediaKey]['target'].toUpperCase() ===
            target.toUpperCase()
        ) {
          updatedFeatures[mediaKey]['use'] = 0;
          if (mediaListInSalesData.indexOf(mediaKey) > -1)
            mediaListInSalesData.splice(
              mediaListInSalesData.indexOf(mediaKey),
              1
            );
        }
      }
    });

    // weeklyAttribtuion mutation (same as ROI Mutation - put it in its own fucntion later):
    let updatedWeeklyData = cloneDeep(state.weeklyData);
    updatedWeeklyData.forEach((mediaObject) => {
      // If target toggle selected as 'All', the grouping values will have their use = 0
      // and the rest will have use = 1. This is similar to the case if no grouping was provided.
      if (value === 'All') {
        if (!!mediaObject['target'] && mediaObject['target'] === target) {
          mediaObject['use'] = 0;
        } else {
          // Make sure that only 'parent' target component is being updated.
          if (mediaObject['name'] === target) mediaObject['use'] = 1;
        }
      } else {
        // This selection goes through media keys and check if 'target' (i.e. TV) is one of the media
        // It will mark 'TV' as 0 for 'use', while everything that has 'target' as TV and group, for example, 'Language'
        // will be marked as 1 for 'use'.
        if (mediaObject['name'].toUpperCase() === target.toUpperCase()) {
          mediaObject['use'] = 0;
        } else if (
          mediaObject['target']?.toUpperCase() === target.toUpperCase() &&
          mediaObject['group']?.toUpperCase() === value.toUpperCase()
        ) {
          mediaObject['use'] = 1;
        } else if (
          !!mediaObject['target'] &&
          mediaObject['target'].toUpperCase() === target.toUpperCase()
        )
          mediaObject['use'] = 0;
      }
    });

    return {
      updatedRoas,
      updatedMediaList,
      updatedRoi,
      updatedSalesData,
      updatedFeatures,
      updatedWeeklyData,
    };
  };

  // This is used in SalesForecast module, with custom data uploads.
  // Custom data comes as an object, so before update the state, the features data
  // needs to be converted to object of objects:
  //  from {BellMedia: [...], Corus: [...]} to {BellMedia: {BellMedia: [], use: 1}, Corus: {Corus: [...], use: 1}}
  const setFeatures = (features) => {
    let updatedFeatures = cloneDeep(features);
    Object.keys(updatedFeatures).forEach((media) => {
      updatedFeatures[media] = {
        [media]: features[media],
        use: 1,
      };
    });
    dispatch({
      type: SET_FEATURES,
      payload: updatedFeatures,
    });
  };

  const setComparedMedia = (comparedMedia) => {
    dispatch({
      type: SET_COMPARED_MEDIA,
      payload: comparedMedia,
    });
  };

  const setForecastRange = (forecastRange) => {
    dispatch({
      type: SET_FORECAST_RANGE,
      payload: forecastRange,
    });
  };

  const setVersion = (version) => {
    dispatch({
      type: SET_VERSION,
      payload: version,
    });
  };

  /**
   * Captures an event for tracking purposes. If the event is a workflow start event,
   * it generates a workflowId and stores it. If it's a workflow end event, it clears
   * the stored workflowId after sending the event.
   *
   * Properties we care about are "mmm_id" and "workflow_id". captureEvent will try to
   * append them from state.workflowId and state.activeModel, but it can be overwritten
   * by extraProperties.
   *
   * @param {string} eventName - The name of the event to track
   * @param {Object} extraProperties - Additional properties to include with the event
   * @returns {string|null} - Returns the workflowId for workflow start events, null otherwise
   */
  const captureEvent = (eventName, extraProperties = {}) => {
    try {
      const isStartEvent =
        eventName === EVENT_NAMES.SDMMM_START ||
        eventName === EVENT_NAMES.SDMMM_EDIT ||
        eventName === EVENT_NAMES.MMM_START;
      const isEndEvent =
        eventName === EVENT_NAMES.SDMMM_BUILD ||
        eventName === EVENT_NAMES.SDMMM_EXIT ||
        eventName === EVENT_NAMES.MMM_EXIT;

      let eventProps = {
        workflow_id: state.workflowId,
        mmm_id: state.activeModel,
      };

      if (isStartEvent) {
        // This is the start of a new workflow. Generate (or overwrite) current workflowId
        const workflowId = uuidv4();
        dispatch({ type: SET_WORKFLOW_ID, payload: workflowId });
        eventProps.workflow_id = workflowId;
      } else if (isEndEvent) {
        // workflowCompleted
        if (eventName === EVENT_NAMES.SDMMM_BUILD) {
          dispatch({ type: SET_WORKFLOW_COMPLETE, payload: true }); // mark as clean termination of workflow
        }
        // This is the end of a workflow. Clear the workflowId to stop tracking the workflow (but can still track events)
        // Delay clearing workflowId to give React time to update workflowCompleted
        setTimeout(() => {
          dispatch({ type: SET_WORKFLOW_ID, payload: null });
        }, 0);
      }

      // Merge extraProperties with eventProps
      eventProps = { ...eventProps, ...extraProperties };
      eventProps.mmm_id = `${eventProps.mmm_id}`; // toString()
      trackerService.captureEvent(eventName, authContext.userInfo, eventProps);
      return eventProps.workflow_id;
    } catch (error) {
      console.error('Error capturing tracker event:', error);
      return null;
    }
  };

  return (
    <MMMContext.Provider
      value={{
        // context
        activeModel: state.activeModel,
        activeAdminModel: state.activeAdminModel,
        salesData: state.salesData,
        roasData: state.roasData,
        weeklyData: state.weeklyData,
        roiData: state.roiData,
        campaignCurve: state.campaignCurve,
        ommData: state.ommData,
        ommMax: state.ommMax,
        ommMin: state.ommMin,
        ommGraphData: state.ommGraphData,
        adstockImpact: state.adstockImpact,
        minDate: state.minDate,
        maxDate: state.maxDate,
        startDate: state.startDate,
        endDate: state.endDate,
        startIndex: state.startIndex,
        endIndex: state.endIndex,
        forecastMinDate: state.forecastMinDate,
        forecastMaxDate: state.forecastMaxDate,
        forecastEndDate: state.forecastEndDate,
        grouping: state.grouping,
        filterMedia: state.filterMedia,
        synergy: state.synergy,
        synergyNew: state.synergyNew,
        synergyDelta: state.synergyDelta,
        exchangeRates: state.exchangeRates,
        currency: state.currency,
        prefixSymbols: state.prefixSymbols,
        filterMediaMinMax: state.filterMediaMinMax,
        filterMediaChoosenRange: state.filterMediaChoosenRange,
        groupingValue: state.groupingValue,
        toggleTargets: state.toggleTargets,
        medias: state.medias,
        features: state.features,
        title: state.title,
        comparedMedia: state.comparedMedia,
        forecastRange: state.forecastRange,
        promotionSliderInfo: state.promotionSliderInfo,
        mediaEntities: state.mediaEntities,
        metric: state.metric,
        modelBuildDate: state.modelBuildDate,
        categories: state.categories,
        selectedCategories: state.selectedCategories,
        version: state.version,
        workflowId: state.workflowId,
        workflowCompleted: state.workflowCompleted,
        exportIntegrations: state.exportIntegrations,
        // methods
        getMMMData,
        getROASProjectionsData,
        getOMMData,
        getOMMGraphData,
        exportInputDataLocal,
        exportMMMDataLocal,
        exportMMMDataCloud,
        getTabFilterUniqueValues,
        getSpreadsheet,
        createTargetGroups,
        setFeatures,
        setStartDate,
        setEndDate,
        setRangeForMedia,
        setFilterMediaAndRange,
        setActiveModel,
        setActiveAdminModel,
        setComparedMedia,
        setSpreadsheet,
        setGroupingValue,
        setCurrency,
        setSelectedCategories,
        setForecastRange,
        setForecastEndDate,
        setVersion,
        resetState,
        resetOMMState,
        captureEvent,
        dispatch,
      }}
    >
      {props.children}
    </MMMContext.Provider>
  );
}

export default MMMState;
